import React, { useState, useEffect } from "react";
import { Phone, Mail, MapPin, Clock, Menu, X } from "lucide-react";
import { companyInfo, navLinks, socialLinks } from "../mock/mock";
import { logoImage } from "../mock/mock";

const FacebookIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M13.397 20.997v-8.196h2.765l.411-3.209h-3.176V7.548c0-.926.258-1.56 1.587-1.56h1.684V3.127A22.336 22.336 0 0 0 14.201 3c-2.444 0-4.122 1.492-4.122 4.231v2.361H7.332v3.209h2.747v8.196h3.318z" />
  </svg>
);
const InstagramIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.058-1.28.073-1.689.073-4.948 0-3.259-.015-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" />
  </svg>
);
const TiktokIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
  </svg>
);

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="sticky top-0 z-50">
      {/* Top utility bar - shown on large screens only to avoid overflow */}
      <div className="hidden lg:block bg-[#0a0a0b] border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-6 py-2 flex items-center justify-between text-[12px] text-[#a9a69d]">
          <div className="flex items-center gap-6 min-w-0">
            <a
              href={`tel:${companyInfo.phone}`}
              className="flex items-center gap-2 hover:text-white transition-colors shrink-0"
            >
              <Phone size={13} />
              <span>{companyInfo.phone}</span>
            </a>
            <span className="h-3 w-px bg-white/10 shrink-0" />
            <a
              href={`mailto:${companyInfo.email}`}
              className="flex items-center gap-2 hover:text-white transition-colors max-w-[220px] truncate min-w-0"
            >
              <Mail size={13} className="shrink-0" />
              <span className="truncate">{companyInfo.email}</span>
            </a>
            <span className="h-3 w-px bg-white/10 shrink-0 hidden xl:block" />
            <div className="hidden xl:flex items-center gap-2 min-w-0">
              <MapPin size={13} className="shrink-0" />
              <span className="truncate max-w-[240px]">
                {companyInfo.location}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            <div className="flex items-center gap-3 text-[#a9a69d]">
              <a
                href={socialLinks.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="hover:text-white"
              >
                <FacebookIcon />
              </a>
              <a
                href={socialLinks.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="hover:text-white"
              >
                <InstagramIcon />
              </a>
              <a
                href={socialLinks.tiktok}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="TikTok"
                className="hover:text-white"
              >
                <TiktokIcon />
              </a>
            </div>
            <span className="h-3 w-px bg-white/10 hidden xl:block" />
            <div className="hidden xl:flex items-center gap-2">
              <Clock size={13} />
              <span>{companyInfo.hours}</span>
            </div>
            <a
              href="#contact"
              className="btn-primary text-[11px] font-semibold tracking-wide px-3 py-1.5 rounded-md whitespace-nowrap"
            >
              Request Free Quote
            </a>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div
        className={`transition-colors duration-300 ${
          scrolled
            ? "bg-[#0a0a0b]/95 backdrop-blur border-b border-white/[0.06]"
            : "bg-[#0a0a0b]/70 backdrop-blur"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 h-[92px] flex items-center justify-between">
          <a href="#" className="flex items-center gap-3">
            <img
              src={logoImage}
              alt="Ortiz Truck & Landscaping LLC"
              className="h-[76px] w-auto object-contain drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]"
            />
            <div className="leading-tight hidden sm:block">
              <div className="font-display font-bold text-[17px] text-[#f4efe6]">
                {companyInfo.name}
              </div>
              <div className="text-[10.5px] tracking-[0.22em] text-[#8a877f] font-medium">
                {companyInfo.tagline}
              </div>
            </div>
          </a>

          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((l) => (
              <a
                key={l.href}
                href={l.href}
                className="text-[12.5px] font-semibold tracking-[0.14em] text-[#c8c5bc] hover:text-white transition-colors"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href="#contact"
              className="hidden md:inline-flex btn-primary text-[11.5px] font-bold tracking-[0.14em] px-4 py-2.5 rounded-md"
            >
              GET DYNAMIC QUOTE
            </a>
            <button
              className="lg:hidden h-10 w-10 rounded-md border border-white/10 flex items-center justify-center text-[#f4efe6]"
              onClick={() => setMobileOpen((s) => !s)}
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="lg:hidden border-t border-white/[0.06] bg-[#0a0a0b]">
            <div className="px-6 py-4 flex flex-col gap-3">
              {navLinks.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setMobileOpen(false)}
                  className="text-[13px] font-semibold tracking-[0.14em] text-[#c8c5bc] py-1"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="#contact"
                onClick={() => setMobileOpen(false)}
                className="btn-primary text-[12px] font-bold tracking-[0.14em] px-4 py-2.5 rounded-md text-center mt-2"
              >
                GET DYNAMIC QUOTE
              </a>
              <div className="pt-4 mt-2 border-t border-white/[0.06] flex flex-col gap-2.5">
                <a
                  href={`tel:${companyInfo.phone}`}
                  className="flex items-center gap-2 text-[13px] text-[#c8c5bc]"
                >
                  <Phone size={13} className="text-[#c9b98a]" />
                  {companyInfo.phone}
                </a>
                <a
                  href={`mailto:${companyInfo.email}`}
                  className="flex items-center gap-2 text-[13px] text-[#c8c5bc] break-all"
                >
                  <Mail size={13} className="text-[#c9b98a] shrink-0" />
                  <span className="break-all">{companyInfo.email}</span>
                </a>
                <div className="flex items-center gap-4 pt-1 text-[#a9a69d]">
                  <a
                    href={socialLinks.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Facebook"
                    className="hover:text-white"
                  >
                    <FacebookIcon />
                  </a>
                  <a
                    href={socialLinks.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Instagram"
                    className="hover:text-white"
                  >
                    <InstagramIcon />
                  </a>
                  <a
                    href={socialLinks.tiktok}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="TikTok"
                    className="hover:text-white"
                  >
                    <TiktokIcon />
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
